import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/cn";

/**
 * Textarea primitive
 *
 * Canonical reference: ianfiles/ui-design-system.md §2.4
 *
 * Shares the `variant: default | flat` shape with Input / SelectTrigger.
 * `flat` is mandatory inside `<SlideDrawer>` / `<FormDrawer>` edit forms.
 *
 * `min-h-[80px]` — never `h-*` — so the textarea can grow when needed.
 * `text-base md:text-sm` matches Input so mixed rows align.
 */
const textareaVariants = cva(
  [
    "flex min-h-[80px] w-full rounded-md px-3 py-2",
    "text-base md:text-sm",
    "ring-offset-background placeholder:text-muted-foreground",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
    "disabled:cursor-not-allowed disabled:opacity-50",
  ].join(" "),
  {
    variants: {
      variant: {
        default: "border border-input bg-background shadow-none",
        flat: "border-0 bg-muted/50 hover:bg-muted shadow-none",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

export interface TextareaProps
  extends React.ComponentProps<"textarea">,
    VariantProps<typeof textareaVariants> {}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, variant, ...props }, ref) => {
    return (
      <textarea
        className={cn(textareaVariants({ variant }), className)}
        ref={ref}
        {...props}
      />
    );
  }
);
Textarea.displayName = "Textarea";

export { Textarea, textareaVariants };
